import { useState, useCallback } from 'react';
import { message } from 'antd';
import { c208Api, type FC150, type FC151 } from '../../../services/c208Api';
import type { Mode } from './useC208Mode';

const emptyMaster = (): FC150 => ({
  fc150_01: '',
  fc150_11: null,
  fc150_12: '',
  fc150_13: '',
  fc150_14: '',
  fc150_15: '',
  fc150_16: '',
  fc150_17: 0,
  fc150_18: 0,
  fc150_19: 0,
  fc150_20: 0,
  fc150_21: '',
  fc150_22: '',
  fc150_23: '',
  fc150_241: '',
  fc150_242: 0,
  fc150_243: '',
  fc150_251: '',
  fc150_252: '',
  fc150_261: 0,
  fc150_262: '',
  fc150_27: '',
  fc150_28: 0,
  fc150_29: '',
  fc150_30: '',
  fc150_31: '',
  fc150_32: '',
  fc150_33: null,
  fc150_34: 0,
  fc150_35: false,
  fc150_36: '',
  fc150_80: 'N',
  fc150_91: '',
  fc150_92: null,
  fc150_93: '',
  fc150_191: 0,
  fc150_192: 0,
  fc150_201: 0,
  fc150_202: 0,
});

const emptyDetail = (seq: number): FC151 => ({
  fc151_01: '',
  fc151_02: seq,
  fc151_11: '',
  fc151_12: '',
  fc151_13: '',
  fc151_21: '',
  fc151_22: '',
});

export function useC208Record(setMode: (m: Mode) => void) {
  const [record, setRecord] = useState<FC150 | null>(null);
  const [formData, setFormData] = useState<FC150>(emptyMaster());
  const [details, setDetails] = useState<FC151[]>([]);
  const [loading, setLoading] = useState(false);

  const loadRecord = useCallback(async (id: string) => {
    setLoading(true);
    try {
      const m = await c208Api.get(id);
      setRecord(m);
      setFormData({ ...m, details: undefined });
      setDetails(m.details ?? []);
    } catch {
      message.error('載入失敗');
    } finally {
      setLoading(false);
    }
  }, []);

  const getField = useCallback(
    <K extends keyof FC150>(key: K): FC150[K] => formData[key],
    [formData]
  );

  const setField = useCallback(<K extends keyof FC150>(key: K, value: FC150[K]) => {
    setFormData((prev) => ({ ...prev, [key]: value }));
  }, []);

  const resetForAdd = useCallback(() => {
    setRecord(null);
    setFormData(emptyMaster());
    setDetails([]);
    setMode('add');
  }, [setMode]);

  const revertToRecord = useCallback(() => {
    if (record) {
      setFormData({ ...record, details: undefined });
      setDetails(record.details ?? []);
    }
    setMode('browse');
  }, [record, setMode]);

  const handleSave = useCallback(
    async (mode: Mode): Promise<boolean> => {
      setLoading(true);
      try {
        const req = { master: { ...formData, details: undefined }, details };
        let saved: FC150;
        if (mode === 'add') {
          saved = await c208Api.create(req);
        } else {
          saved = await c208Api.update(formData.fc150_01, req);
        }
        setRecord(saved);
        setFormData({ ...saved, details: undefined });
        setDetails(saved.details ?? []);
        setMode('browse');
        message.success('存檔成功');
        return true;
      } catch (err: unknown) {
        const axiosErr = err as { response?: { data?: { errors?: { message: string }[] } } };
        const errs = axiosErr?.response?.data?.errors;
        if (errs?.length) {
          message.error(errs.map((e) => e.message).join('\n'));
        } else {
          message.error('存檔失敗');
        }
        return false;
      } finally {
        setLoading(false);
      }
    },
    [formData, details, setMode]
  );

  const handleDelete = useCallback(
    async (id: string): Promise<boolean> => {
      setLoading(true);
      try {
        await c208Api.delete(id);
        setRecord(null);
        setFormData(emptyMaster());
        setDetails([]);
        setMode('browse');
        message.success('刪除成功');
        return true;
      } catch {
        message.error('刪除失敗');
        return false;
      } finally {
        setLoading(false);
      }
    },
    [setMode]
  );

  const addDetailRow = useCallback(() => {
    setDetails((prev) => {
      if (prev.length >= 99) return prev;
      return [...prev, emptyDetail(prev.length + 1)];
    });
  }, []);

  const deleteDetailRow = useCallback((idx: number) => {
    setDetails((prev) => {
      const next = prev.filter((_, i) => i !== idx);
      return next.map((d, i) => ({ ...d, fc151_02: i + 1 }));
    });
  }, []);

  const updateDetailField = useCallback(
    <K extends keyof FC151>(idx: number, key: K, value: FC151[K]) => {
      setDetails((prev) =>
        prev.map((d, i) => (i === idx ? { ...d, [key]: value } : d))
      );
    },
    []
  );

  return {
    record,
    formData,
    details,
    loading,
    loadRecord,
    getField,
    setField,
    resetForAdd,
    revertToRecord,
    handleSave,
    handleDelete,
    addDetailRow,
    deleteDetailRow,
    updateDetailField,
  };
}
